import React, { useMemo, useRef, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import { type LexicalEditor } from 'lexical'
import { $generateHtmlFromNodes } from '@lexical/html'

import { Modal } from '../../../../components/Modal/Modal'
import { SelectDropdown, type SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'

import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../../../store/auth/authSlice'
import { useGetConnectorEntitiesQuery, type SalesforceUserDefinedLabel } from '../../../../store/integrations/integrations'
import { getIntegrationDisplayName, type IntegrationServiceId } from '../../../../common/integrations'
import { toLocalISODate } from '../../../../common/dateUtils'

import { TagsModal, type TagOption } from '../TagsModal'
import { type CrmActionModalProps } from '../crmActionRegistry'

import { useResolvedResults } from './useResolvedResults'
import { useCrmEligibility } from './useCrmEligibility'
import { useCrmActionSave } from './useCrmActionSave'
import { CrmEligibilityNotice, CrmLoadingBody, CrmNoEligibleAlert } from './CrmEligibilityFeedback'
import { DueDateInput } from './DueDateInput'
import { CrmRichTextEditor } from './CrmRichTextEditor'
import { useCrmAssignee } from './useCrmAssignee'

// Salesforce and its overlays (BlackDiamond Edge, Practifi, BlackDiamond Elements, XLR8) all sit on
// the same Salesforce data model, so their note/task actions are identical apart from the integration
// they target and the integration name woven into the copy. These factories build the modals from a
// single `serviceId`; the only per-integration difference is the resolved display name.

const countLabel = (count: number) => `${count} ${count === 1 ? 'client' : 'clients'}`

export const createSalesforceStyleNoteModal = (serviceId: IntegrationServiceId): React.FC<CrmActionModalProps> => {
  const crmName = getIntegrationDisplayName(serviceId)

  const SalesforceStyleNoteModal: React.FC<CrmActionModalProps> = ({ open, onClose, resolveResults, tracking }) => {
    const { results, resolving } = useResolvedResults(resolveResults)
    const eligibility = useCrmEligibility(serviceId, results ?? [])

    const { save, saving } = useCrmActionSave({
      tracking,
      eligibility,
      onClose,
      serviceId,
      sourceObject: 'note',
      entityType: 'notes',
      relatedToType: 'Contact',
      successToast: { title: 'Adding note', message: `${crmName} note creation started.` },
      errorToast: { title: 'Failed to add note', message: `Could not start the ${crmName} note creation. Please try again.` },
    })

    const editorRef = useRef<LexicalEditor>(null)
    const [textContent, setTextContent] = useState('')

    const targetLabel = countLabel(results?.length ?? 0)

    const noEligibleClients = !resolving && eligibility.status === 'ready' && eligibility.eligibleCount === 0

    const body =
      resolving || eligibility.status === 'loading' ? (
        <CrmLoadingBody crmName={crmName} />
      ) : noEligibleClients ? (
        <CrmNoEligibleAlert crmName={crmName} />
      ) : (
        <div className='d-flex flex-column gap-4'>
          <div className='paragraph-medium'>This will run in the background. Feel free to close this window or continue with other tasks.</div>
          <CrmRichTextEditor ref={editorRef} variant='note' onTextContentChange={setTextContent} placeholder={<>Enter a note for selected clients…</>} />
          <CrmEligibilityNotice eligibility={eligibility} verbPhrase='add a note to' crmName={crmName} />
        </div>
      )

    const handleSave = async () => {
      const editor = editorRef.current
      if (!editor) {
        return
      }
      const html = editor.getEditorState().read(() => $generateHtmlFromNodes(editor, null))
      await save({ note: html })
    }

    const primaryActionDisabled = noEligibleClients
      ? false
      : resolving || eligibility.status !== 'ready' || eligibility.eligibleCount === 0 || textContent.trim().length === 0

    return (
      <Modal
        show={open}
        onHide={onClose}
        header={<h4>Add note to {targetLabel}</h4>}
        body={body}
        headerBodySeparator={true}
        showFooterActions={true}
        footerClassName='px-4 pb-4 pt-0'
        onFooterPrimaryAction={noEligibleClients ? onClose : handleSave}
        footerPrimaryActionTitle={noEligibleClients ? 'Done' : 'Add note'}
        footerPrimaryActionDisabled={primaryActionDisabled}
        footerPrimaryActionLoading={saving}
        onFooterSecondaryAction={onClose}
        hideSecondaryAction={noEligibleClients}
        centered={true}
      />
    )
  }

  SalesforceStyleNoteModal.displayName = `SalesforceStyleNoteModal(${serviceId})`
  return SalesforceStyleNoteModal
}

export const createSalesforceStyleTagsModal = (serviceId: IntegrationServiceId): React.FC<CrmActionModalProps> => {
  const crmName = getIntegrationDisplayName(serviceId)

  const SalesforceStyleTagsModal: React.FC<CrmActionModalProps> = ({ open, onClose, resolveResults, tracking }) => {
    const accountId = useAppSelector(selectAccountId)

    const { results, resolving } = useResolvedResults(resolveResults)
    const eligibility = useCrmEligibility(serviceId, results ?? [])

    const { save, saving } = useCrmActionSave({
      tracking,
      eligibility,
      onClose,
      serviceId,
      sourceObject: 'tags',
      entityType: 'UserDefinedLabelAssignment',
      relatedToType: 'Contact',
      successToast: { title: 'Assigning labels', message: `${crmName} label assignment started.` },
      errorToast: { title: 'Failed to assign labels', message: `Could not start the ${crmName} label assignment. Please try again.` },
    })

    const { data, isFetching } = useGetConnectorEntitiesQuery({
      accountId,
      connector: serviceId,
      entityType: 'tags',
      relatedTo: null,
    })

    const tags: TagOption[] = useMemo(() => {
      const payload = data?.data as Array<{ tags?: SalesforceUserDefinedLabel[] }> | undefined
      const rawTags = payload?.flatMap((entry) => entry.tags ?? []) ?? []
      return rawTags.map((label) => ({ id: label.id, name: label.name }))
    }, [data])

    const totalCount = results?.length ?? 0
    const noEligibleClients = !resolving && eligibility.status === 'ready' && eligibility.eligibleCount === 0

    const bodyOverride =
      resolving || eligibility.status === 'loading' ? <CrmLoadingBody crmName={crmName} /> : noEligibleClients ? <CrmNoEligibleAlert crmName={crmName} /> : null

    const primaryActionDisabled = resolving || eligibility.status !== 'ready' || eligibility.eligibleCount === 0

    const handleSave = async (selectedTags: TagOption[]) => {
      await save({ tags: selectedTags.map((t) => ({ id: t.id, name: t.name })) })
    }

    return (
      <TagsModal
        show={open}
        onCancel={onClose}
        tags={tags}
        loading={isFetching}
        saving={saving}
        count={totalCount}
        onSave={handleSave}
        primaryActionTitle={noEligibleClients ? 'Done' : 'Assign labels'}
        notice={<CrmEligibilityNotice eligibility={eligibility} verbPhrase='label' crmName={crmName} />}
        primaryActionDisabled={noEligibleClients ? false : primaryActionDisabled}
        bodyOverride={bodyOverride}
        hideSecondaryAction={noEligibleClients}
        primaryActionOverride={noEligibleClients ? onClose : undefined}
        entityLabel='labels'
      />
    )
  }

  SalesforceStyleTagsModal.displayName = `SalesforceStyleTagsModal(${serviceId})`
  return SalesforceStyleTagsModal
}

const PRIORITY_OPTIONS: SelectOption[] = [
  { id: 'High', label: 'High' },
  { id: 'Normal', label: 'Normal' },
  { id: 'Low', label: 'Low' },
]
const DEFAULT_PRIORITY_OPTION = PRIORITY_OPTIONS[1]

export const createSalesforceStyleTaskModal = (serviceId: IntegrationServiceId): React.FC<CrmActionModalProps> => {
  const crmName = getIntegrationDisplayName(serviceId)

  const SalesforceStyleTaskModal: React.FC<CrmActionModalProps> = ({ open, onClose, resolveResults, tracking }) => {
    const { results, resolving } = useResolvedResults(resolveResults)
    const eligibility = useCrmEligibility(serviceId, results ?? [])

    const { save, saving } = useCrmActionSave({
      tracking,
      eligibility,
      onClose,
      serviceId,
      sourceObject: 'task',
      entityType: 'task',
      relatedToType: 'Contact',
      successToast: { title: 'Creating task', message: `${crmName} task creation started.` },
      errorToast: { title: 'Failed to create task', message: `Could not start the ${crmName} task creation. Please try again.` },
    })

    const [subject, setSubject] = useState('')
    const descriptionEditorRef = useRef<LexicalEditor>(null)
    const [descriptionText, setDescriptionText] = useState('')
    const [dueDate, setDueDate] = useState<Date | null>(null)
    const [selectedPriority, setSelectedPriority] = useState<SelectOption>(DEFAULT_PRIORITY_OPTION)

    // `zocksAssignedTo` is the current user (the task creator); the picked teammate only
    // supplies the external `assignedTo` for the CRM.

    const currentUser = useAppSelector(selectAuthenticatedUser)
    const targetLabel = `${countLabel(results?.length ?? 0)} in ${crmName}`

    const noEligibleClients = !resolving && eligibility.status === 'ready' && eligibility.eligibleCount === 0

    const clientOwner: string | undefined =
      eligibility.status === 'ready' && eligibility.eligibleCount === 1 ? eligibility.eligibleTargets[0].ownerId : undefined
    const { selectedAssignee, selectedAssigneeExternalId, assigneeOptions, selectedAssigneeOption, handleAssigneeOptionSelected } = useCrmAssignee(
      serviceId,
      clientOwner,
    )

    // Subject, assignee, and due date are required; description is optional. `selectedPriority` always holds a value.
    const isFormValid = subject.trim().length > 0 && dueDate !== null && selectedAssigneeExternalId !== null

    const handleSave = async () => {
      if (!isFormValid) {
        return
      }

      const zocksTask: Record<string, unknown> = {
        task: subject.trim(),
        priority: selectedPriority.id,
        status: 'open',
      }
      const editor = descriptionEditorRef.current
      if (editor && descriptionText.trim().length > 0) {
        zocksTask.description = editor.getEditorState().read(() => $generateHtmlFromNodes(editor, null))
      }
      if (dueDate) {
        zocksTask.dueDate = toLocalISODate(dueDate)
      }
      if (selectedAssignee && selectedAssigneeExternalId) {
        // The picked teammate only supplies the external CRM assignee.
        zocksTask.assignedTo = selectedAssigneeExternalId
      }
      // `zocksAssignedTo` is the current user, required by integrations-service SaveTaskDetailsDTO.
      if (currentUser?.id) {
        zocksTask.zocksAssignedTo = {
          type: 'internal',
          id: currentUser.id,
          user: {
            name: currentUser.name,
            type: 'internal',
          },
        }
      }

      await save({ tasks: [zocksTask] })
    }

    const body =
      resolving || eligibility.status === 'loading' ? (
        <CrmLoadingBody crmName={crmName} />
      ) : noEligibleClients ? (
        <CrmNoEligibleAlert crmName={crmName} />
      ) : (
        <div className='d-flex flex-column gap-3'>
          <div className='paragraph-medium'>This will run in the background. Feel free to close this window or continue with other tasks.</div>
          <Form.Group controlId={`${serviceId}TaskSubject`} className='d-flex flex-column'>
            <Form.Label className='bold'>
              Task name <span className='text-danger'>*</span>
            </Form.Label>
            <Form.Control value={subject} onChange={(e) => setSubject(e.target.value)} placeholder='Enter task name' disabled={saving} />
          </Form.Group>
          <Form.Group controlId={`${serviceId}TaskDescription`} className='d-flex flex-column'>
            <Form.Label className='bold'>Task description</Form.Label>
            <CrmRichTextEditor ref={descriptionEditorRef} variant='task' onTextContentChange={setDescriptionText} placeholder={<>Enter task description…</>} />
          </Form.Group>
          <Row className='g-3'>
            <Form.Group as={Col} controlId={`${serviceId}TaskAssignee`} xs={12} md={6} className='d-flex flex-column'>
              <Form.Label className='bold'>
                Assignee <span className='text-danger'>*</span>
              </Form.Label>
              <SelectDropdown
                className='py-2 zocks-px-0.75'
                options={assigneeOptions}
                initialSelectedOption={selectedAssigneeOption}
                onOptionSelected={handleAssigneeOptionSelected}
              />
            </Form.Group>
            <Form.Group as={Col} controlId={`${serviceId}TaskDueDate`} xs={12} md={6} className='d-flex flex-column'>
              <Form.Label className='bold'>
                Due date <span className='text-danger'>*</span>
              </Form.Label>
              <DatePicker
                selected={dueDate}
                onChange={(date: Date | null) => setDueDate(date)}
                todayButton='Today'
                dateFormat='dd/MM/yyyy'
                disabled={saving}
                customInput={<DueDateInput />}
              />
            </Form.Group>
            <Form.Group as={Col} controlId={`${serviceId}TaskPriority`} xs={12} md={6} className='d-flex flex-column'>
              <Form.Label className='bold'>Priority</Form.Label>
              <SelectDropdown
                className='py-2 zocks-px-0.75'
                options={PRIORITY_OPTIONS}
                initialSelectedOption={selectedPriority}
                onOptionSelected={setSelectedPriority}
              />
            </Form.Group>
          </Row>
          <CrmEligibilityNotice eligibility={eligibility} verbPhrase='create a task for' crmName={crmName} />
        </div>
      )

    const primaryActionDisabled = noEligibleClients ? false : resolving || eligibility.status !== 'ready' || eligibility.eligibleCount === 0 || !isFormValid

    return (
      <Modal
        show={open}
        onHide={onClose}
        header={<h4>Create task to {targetLabel}</h4>}
        body={body}
        headerBodySeparator={true}
        showFooterActions={true}
        footerClassName='px-4 pb-4 pt-0'
        onFooterPrimaryAction={noEligibleClients ? onClose : handleSave}
        footerPrimaryActionTitle={noEligibleClients ? 'Done' : 'Create task'}
        footerPrimaryActionDisabled={primaryActionDisabled}
        footerPrimaryActionLoading={saving}
        onFooterSecondaryAction={onClose}
        hideSecondaryAction={noEligibleClients}
        centered={true}
      />
    )
  }

  SalesforceStyleTaskModal.displayName = `SalesforceStyleTaskModal(${serviceId})`
  return SalesforceStyleTaskModal
}
